/**
 * A class, as a plate in the atlas.
 *
 * The page's light is mixed from exactly the subjects this class contains, so a
 * Class 10 plate glows teal + violet + amber and a single-subject class glows
 * its one hue. On that wash: a glass hero plate carrying the class numeral in
 * editorial serif, then one glass REGION per subject — tinted by that subject's
 * own colour — with its lessons floating inside as thumbnail cards.
 *
 * Status reads on the card: a progress bar in the subject's hue while watching,
 * a "Done" mark when complete, and "no check yet" stated rather than implied by
 * a blank.
 */

import { createFileRoute, Link, notFound } from '@tanstack/react-router';
import { Check, Play, GraduationCap, ArrowLeft, Clock } from 'lucide-react';
import { cn } from '../lib/utils';
import { fetchCourse, type CourseLesson } from '../services/learnAPI';
import {
  LearnRoot,
  LearnTopBar,
  useLearnTheme,
  subjectMeta,
  SubjectArt,
  glassHero,
  glassPanel,
  glassChip,
  inkMuted,
} from '../components/learn/learnShared';
import { LearnAccountMenu } from '../components/learn/LearnAccountMenu';
import { useLearnerProgress, type LearnerProgressState } from '../components/learn/useLearnerProgress';
import { useLearnerAccess } from '../components/learn/useLearnerAccess';

export const Route = createFileRoute('/learn/course/$courseId')({
  component: CoursePage,
  loader: async ({ params }) => {
    const detail = await fetchCourse(params.courseId);
    if (!detail) throw notFound();
    return detail;
  },
  notFoundComponent: () => (
    <div className="grid min-h-screen place-items-center bg-[#FBFAF7] text-center text-[#191831] dark:bg-[#0D0E16] dark:text-[#EFEEF8]">
      <div className="space-y-3">
        <h1 className="font-serif text-3xl">Course not found</h1>
        <Link to="/learn" className="inline-block font-semibold text-[#5B2EE5] underline dark:text-[#B69BF5]">
          Back to your classes
        </Link>
      </div>
    </div>
  ),
});

const minutes = (s: number) => (s ? `${Math.round(s / 60)} min` : '');

/** The action violet — one button per screen carries it. */
const primaryAction =
  'inline-flex items-center gap-2 rounded-full bg-[#5B2EE5] px-6 py-3 text-sm font-semibold text-white ' +
  'shadow-[0_10px_26px_-10px_rgba(91,46,229,0.85)] transition-colors hover:bg-[#4D22D2] ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5B2EE5]';

const eyebrow = 'text-[10.5px] font-bold uppercase tracking-[0.19em]';

function CoursePage() {
  const { course, chapters: allChapters } = Route.useLoaderData();
  const access = useLearnerAccess();
  // A class the learner only partly holds shows the part they hold — and a
  // subject left with nothing drops out rather than rendering an empty band.
  const chapters = allChapters
    .map((c) => ({ ...c, lessons: c.lessons.filter((l) => access.visible(l.shareToken)) }))
    .filter((c) => c.lessons.length > 0);
  const { theme, toggle } = useLearnTheme();
  const progress = useLearnerProgress();
  const { byToken, loading } = progress;

  const all = chapters.flatMap((c) => c.lessons);
  const done = all.filter((l) => byToken.get(l.shareToken)?.status === 'completed').length;
  const next = all.find((l) => byToken.get(l.shareToken)?.status !== 'completed');
  const glyph = course.title.match(/(\d{1,2})/)?.[1] ?? course.title.charAt(0);
  // The aurora is mixed from exactly the subjects this class contains.
  const subjects = chapters.map((c) => c.title ?? '').filter(Boolean);
  const pct = all.length ? Math.round((done / all.length) * 100) : 0;

  // Check facts, only when this class actually has check data to show.
  const checksTaken = all.filter((l) => byToken.get(l.shareToken)?.linkedTest?.taken).length;
  const bestCheck = all.reduce<number | null>((best, l) => {
    const s = byToken.get(l.shareToken)?.linkedTest?.bestScorePercent;
    return s != null && (best == null || s > best) ? s : best;
  }, null);

  const facts: { k: string; v: string }[] = [
    { k: 'Lessons', v: loading ? String(all.length) : `${done} of ${all.length}` },
    { k: chapters.length === 1 ? 'Subject' : 'Subjects', v: String(chapters.length) },
    ...(checksTaken > 0 ? [{ k: 'Checks taken', v: String(checksTaken) }] : []),
    ...(bestCheck != null ? [{ k: 'Best check', v: `${bestCheck}%` }] : []),
  ];

  return (
    <LearnRoot theme={theme} subjects={subjects}>
      <LearnTopBar
        theme={theme}
        onToggle={toggle}
        right={
          <>
            <Link
              to="/learn"
              className={cn(
                'mr-1 hidden items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium transition-colors sm:inline-flex',
                'hover:text-[#191831] dark:hover:text-white',
                inkMuted,
              )}
            >
              <ArrowLeft className="h-4 w-4" /> All classes
            </Link>
            <LearnAccountMenu />
          </>
        }
      />

      <main className="learn-shell mx-auto w-full px-4 pb-24 pt-6 sm:px-6">
        {/* The hero plate: the class numeral, its light, its standing. */}
        <header className={cn(glassHero, 'px-5 py-7 sm:px-9 sm:py-9')}>
          <div className="flex flex-col gap-6 md:flex-row md:items-start md:gap-10">
            <span
              aria-hidden="true"
              className="block shrink-0 select-none pb-[0.06em] font-serif text-[86px] tracking-[-0.045em] sm:text-[132px] lg:text-[176px]"
              style={{ lineHeight: 0.84 }}
            >
              {glyph}
            </span>

            <div className="min-w-0 flex-1 md:pt-4">
              <p className={cn(eyebrow, inkMuted)}>Atlas plate</p>
              <h1 className="mt-2 font-serif text-[34px] leading-[1.14] tracking-[-0.015em] sm:text-[44px]">
                {course.title}
              </h1>
              <p className={cn('mt-2.5 max-w-[52ch] text-[15px] leading-relaxed', inkMuted)}>
                {course.description ??
                  `${all.length} interactive lesson${all.length === 1 ? '' : 's'} across ${chapters.length} subject${chapters.length === 1 ? '' : 's'}.`}
              </p>

              <dl className="mt-5 flex flex-wrap gap-2.5">
                {facts.map((f) => (
                  <div
                    key={f.k}
                    className="rounded-2xl border border-white/70 bg-white/70 px-4 py-2.5 dark:border-white/10 dark:bg-white/[0.07]"
                  >
                    <dt className={cn(eyebrow, inkMuted)}>{f.k}</dt>
                    <dd className="mt-0.5 font-serif text-[22px] leading-[1.28]">{f.v}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-6 flex flex-wrap items-center gap-4">
                {next && (
                  <Link to="/learn/$shareToken" params={{ shareToken: next.shareToken }} className={primaryAction}>
                    <Play className="h-4 w-4 fill-current" />
                    {done === 0 ? 'Start the first lesson' : 'Continue'}
                  </Link>
                )}
                {!loading && (
                  <span className={cn('text-[13px] font-semibold', inkMuted)}>
                    {done} of {all.length} completed
                  </span>
                )}
              </div>
            </div>

            {!loading && all.length > 0 && (
              <div className="hidden shrink-0 flex-col items-center md:flex md:pt-4">
                <ProgressRing pct={pct} />
                <span className="mt-2 font-serif text-[22px] leading-none">{pct}%</span>
                <span className={cn('mt-1 text-[12.5px]', inkMuted)}>complete</span>
              </div>
            )}
          </div>
        </header>

        {chapters.map((chapter) => (
          <RegionSection
            key={chapter.title ?? '—'}
            title={chapter.title}
            lessons={chapter.lessons}
            progress={progress}
          />
        ))}
      </main>
    </LearnRoot>
  );
}

/** Completion, as a ring. Pure presentation of a number we already hold. */
function ProgressRing({ pct }: { pct: number }) {
  const r = 19;
  const c = 2 * Math.PI * r;
  return (
    <svg width="104" height="104" viewBox="0 0 46 46" className="-rotate-90" aria-hidden="true">
      <circle cx="23" cy="23" r={r} fill="none" strokeWidth="3" className="stroke-[#191831]/[0.14] dark:stroke-white/20" />
      <circle
        cx="23"
        cy="23"
        r={r}
        fill="none"
        strokeWidth="3"
        strokeLinecap="round"
        stroke="#5B2EE5"
        strokeDasharray={`${(c * pct) / 100} ${c}`}
      />
    </svg>
  );
}

/**
 * One subject = one region of the plate, tinted by that subject's own hue.
 * A region holding a single lesson lays that lesson out full-width rather than
 * stranding one card in a three-column grid.
 */
function RegionSection({
  title,
  lessons,
  progress,
}: {
  title: string | null;
  lessons: CourseLesson[];
  progress: LearnerProgressState;
}) {
  const meta = subjectMeta(title);
  const done = lessons.filter((l) => progress.byToken.get(l.shareToken)?.status === 'completed').length;
  const wide = lessons.length === 1;

  return (
    <section className={cn(glassPanel, 'relative mt-5 overflow-hidden p-5 sm:p-6')}>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-[inherit]"
        style={{ background: `linear-gradient(115deg, ${meta.color}30, ${meta.color}0D 48%, transparent 78%)` }}
      />

      <div className="relative mb-4 flex items-center gap-3.5">
        <span className="h-[11px] w-[11px] shrink-0 rounded-full" style={{ background: meta.color }} />
        <h2 className="min-w-0 truncate font-serif text-[22px] leading-[1.28] tracking-[-0.01em] sm:text-[27px]">
          {title ?? 'More lessons'}
        </h2>
        <span className="h-px min-w-[16px] flex-1 bg-[#191831]/[0.14] dark:bg-white/[0.14]" />
        <span className={cn(eyebrow, 'shrink-0 tracking-[0.14em]', inkMuted)}>
          {done} of {lessons.length}
        </span>
      </div>

      <div
        className={cn(
          'relative grid gap-4',
          wide ? 'grid-cols-1' : lessons.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
        )}
      >
        {lessons.map((lesson) => (
          <LessonCard key={lesson.shareToken} lesson={lesson} subject={title} progress={progress} wide={wide} />
        ))}
      </div>
    </section>
  );
}

function LessonCard({
  lesson,
  subject,
  progress,
  wide,
}: {
  lesson: CourseLesson;
  subject: string | null;
  progress: LearnerProgressState;
  wide: boolean;
}) {
  const p = progress.byToken.get(lesson.shareToken);
  const completed = p?.status === 'completed';
  const started = p && p.status !== 'not_started';
  const meta = subjectMeta(subject);

  return (
    <Link
      to="/learn/$shareToken"
      params={{ shareToken: lesson.shareToken }}
      className={cn(
        glassPanel,
        'group flex flex-col overflow-hidden rounded-[18px] p-2.5 transition-transform duration-200',
        'hover:-translate-y-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5B2EE5]',
        'motion-reduce:transition-none motion-reduce:hover:translate-y-0',
        wide && 'sm:flex-row sm:items-stretch sm:gap-4',
      )}
    >
      <div
        className={cn(
          'relative aspect-video shrink-0 overflow-hidden rounded-[14px] bg-[#151427]',
          wide && 'sm:w-[46%]',
        )}
      >
        {lesson.thumbnailUrl ? (
          <img
            src={lesson.thumbnailUrl}
            alt=""
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
          />
        ) : (
          <SubjectArt subject={subject} />
        )}

        <span className="absolute inset-0 grid place-items-center bg-black/25 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100 motion-reduce:transition-none">
          <span className="grid h-12 w-12 place-items-center rounded-full bg-white/95 shadow-lg">
            <Play className="h-5 w-5 fill-[#191831] text-[#191831]" />
          </span>
        </span>

        {lesson.totalDuration > 0 && (
          <span className="absolute bottom-2.5 right-2.5 inline-flex items-center gap-1 rounded-full bg-black/65 px-2 py-0.5 text-[11px] font-semibold text-white">
            <Clock className="h-3 w-3" /> {minutes(lesson.totalDuration)}
          </span>
        )}

        {completed && (
          <span className="absolute left-2.5 top-2.5 inline-flex items-center gap-1 rounded-full bg-[#0B5B52] px-2.5 py-1 text-[11px] font-bold text-white shadow">
            <Check className="h-3 w-3" /> Done
          </span>
        )}

        {started && !completed && (
          <span className="absolute bottom-0 left-0 right-0 h-[4px] bg-black/45">
            <span className="block h-full" style={{ width: `${Math.max(5, p!.maxPercent)}%`, background: meta.bright }} />
          </span>
        )}
      </div>

      <div className={cn('flex flex-1 flex-col px-1.5 pb-1 pt-3', wide && 'sm:justify-center sm:py-3 sm:pr-3')}>
        <p className={cn(eyebrow, 'tracking-[0.13em]', inkMuted)}>{subject ?? 'Lesson'}</p>
        <h3
          className={cn(
            'mt-1.5 line-clamp-2 break-words font-serif text-[19px] leading-[1.2] tracking-[-0.005em]',
            wide && 'sm:text-[24px]',
          )}
        >
          {lesson.title ?? 'Untitled lesson'}
        </h3>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-[11.5px] font-semibold">
          {started && !completed && (
            <span
              className={cn(glassChip, 'inline-flex items-center gap-1.5 px-2.5 py-1')}
              style={{ color: meta.color }}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-current" />
              <span className="dark:brightness-150">{p!.maxPercent}% watched</span>
            </span>
          )}
          {lesson.hasCheck ? (
            <span
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1',
                p?.linkedTest?.taken
                  ? 'bg-[#0B5B52]/[0.14] text-[#0B5B52] dark:bg-[#2DD4BF]/[0.16] dark:text-[#7FE7DA]'
                  : cn(glassChip, inkMuted),
              )}
            >
              <GraduationCap className="h-3 w-3" />
              {p?.linkedTest?.taken
                ? p.linkedTest.bestScorePercent != null
                  ? `Check ${p.linkedTest.bestScorePercent}%`
                  : 'Check done'
                : 'Has a check'}
            </span>
          ) : (
            <span className={cn(glassChip, 'px-2.5 py-1 font-medium', inkMuted)}>no check yet</span>
          )}
        </div>
      </div>
    </Link>
  );
}
